import { memo, type JSX } from 'react';
import { Box, IconButton, styled } from '@mui/material';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import {
  CHART_LANE_CONTROL_ICON_SIZE,
  CHART_LANE_Y_AXIS_WIDTH,
  DEFAULT_ASSAY_FILTER_STATE,
} from '@/constants/PatientDashboard/patientTimeline';
import type {
  ChartAssay,
  IAssayFilterState,
  IDiseaseBurdenAnalyte,
  InformedMode,
} from '@/types/PatientDashboard/PatientTimeline.types';
import AssayFilterRow from './AssayFilterRow';
import ChartSeriesGlyph from './ChartSeriesGlyph';
import { LaneLabelColumn, LaneRow, LANE_BORDERS } from './ChartLane.styles';

const ICON_STROKE_WIDTH = 1.8;
const ASSAYS_LABEL_LETTER_SPACING = '0.04em';

const Panel = styled(LaneLabelColumn)({
  gap: '6px',
  padding: `8px ${CHART_LANE_Y_AXIS_WIDTH}px 8px 8px`,
  ...LANE_BORDERS,
});

const AnalyteName = styled(CustomTypography)({
  flex: 1,
  minWidth: 0,
  color: corePalette.offBlack100,
});

const VisibilityButton = styled(IconButton)({
  padding: 0,
  flexShrink: 0,
  width: `${CHART_LANE_CONTROL_ICON_SIZE}px`,
  height: `${CHART_LANE_CONTROL_ICON_SIZE}px`,
  borderRadius: 0,
  color: corePalette.grey100,
  '&:hover': {
    background: 'transparent',
    color: corePalette.grey200,
  },
  '& svg': {
    display: 'block',
  },
});

const AssaysLabel = styled(CustomTypography)({
  color: corePalette.grey100,
  letterSpacing: ASSAYS_LABEL_LETTER_SPACING,
});

const AssayList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

interface IProps {
  analyte: IDiseaseBurdenAnalyte;
  filters: Record<string, IAssayFilterState> | undefined;
  isVisible: boolean;
  onToggleAssay: (analyte: string, assay: ChartAssay) => void;
  onSetMode: (analyte: string, assay: ChartAssay, mode: InformedMode) => void;
  onToggleVisibility: (analyte: string) => void;
}

function AnalyteControlPanel({
  analyte,
  filters,
  isVisible,
  onToggleAssay,
  onSetMode,
  onToggleVisibility,
}: IProps): JSX.Element {
  const handleToggleVisibility = (): void => onToggleVisibility(analyte.analyte);

  return (
    <Panel>
      <LaneRow>
        <ChartSeriesGlyph colour={analyte.colour} />
        <AnalyteName
          truncate
          tooltipText={analyte.analyte}
          variant="bodySmall"
          fontWeight="medium"
        >
          {analyte.analyte}
        </AnalyteName>
        <VisibilityButton
          onClick={handleToggleVisibility}
          disableRipple
          aria-label={`${isVisible ? 'Hide' : 'Show'} ${analyte.analyte}`}
        >
          {isVisible
            ? <EyeIcon size={CHART_LANE_CONTROL_ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />
            : <EyeOffIcon size={CHART_LANE_CONTROL_ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />}
        </VisibilityButton>
      </LaneRow>

      {isVisible && (
        <>
          <AssaysLabel variant="bodyTiny" fontWeight="bold">
            ASSAYS
          </AssaysLabel>

          <AssayList>
            {analyte.assays.map((assay) => (
              <AssayFilterRow
                key={assay}
                analyte={analyte.analyte}
                assay={assay}
                colour={analyte.colour}
                isSelected={Boolean(filters?.[assay]?.isSelected)}
                mode={filters?.[assay]?.mode ?? DEFAULT_ASSAY_FILTER_STATE.mode}
                onToggle={onToggleAssay}
                onSetMode={onSetMode}
              />
            ))}
          </AssayList>
        </>
      )}
    </Panel>
  );
}

export default memo(AnalyteControlPanel);
